import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Card, CardBody, Table, Button, Container } from "reactstrap";
import { toast } from "react-toastify";
import moment from "moment";

import Loaders from "components/Common/Loaders";
import {
  fetchBackendDashboard,
  fetchBackendDashboardDetails,
} from "model/DashboardModel";
import { urlEndPoint } from "helpers/GeneralHelper";

import "./backend-dashboard.scss";

const REVIEW_STATUS_TONE = {
  1: "red",
  2: "yellow",
  3: "green",
};

const REVIEW_STATUS_LABEL = {
  1: "Red",
  2: "Yellow",
  3: "Green",
};

const CALL_EXECUTIVE_TODAY_PREFILL_STORAGE_KEY = "callExecutiveTodayPrefillFilters";

const STATIC_STATUS_CARDS = [
  {
    key: "eligibleDrugTestOrMecPending",
    title: "Eligible",
    subtitle: "Drug Test Or MEC Pending",
    tone: "orange",
    prefill: { orderStatus: "eligible" },
  },
  {
    key: "inProgress",
    title: "In Progress",
    subtitle: "",
    tone: "blue",
    prefill: { orderStatus: "in progress" },
  },
  {
    key: "started",
    title: "New Leads Added",
    subtitle: "",
    tone: "green",
    prefill: { callStatus: "not called" },
  },
  {
    key: "vmsFollowUps",
    title: "VM's/Follow Ups",
    subtitle: "",
    tone: "teal",
    staticValue: true,
    prefill: { callStatus: "vm" },
  },
];

const formatDashboardDate = (value) => {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("MM-DD-YYYY") : "";
};

const formatDisplayValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

const formatMetricCell = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
};

const formatTimezoneLabel = (timeZone) => {
  const value = String(timeZone ?? "").trim();
  return value ? value.replace(/_/g, " ") : "—";
};

const formatLastReportSendRelative = (value) => {
  if (!value) return "—";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.fromNow() : "—";
};

const formatTargetRange = (min, max) => {
  const hasMin = min !== null && min !== undefined && min !== "";
  const hasMax = max !== null && max !== undefined && max !== "";
  if (!hasMin && !hasMax) return "—";
  if (hasMin && hasMax) return `${min} - ${max}`;
  return String(hasMin ? min : max);
};

const formatAchievedValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

const resolveReviewCardTone = (reviewStatusId) =>
  REVIEW_STATUS_TONE[String(reviewStatusId ?? "").trim()] || "neutral";

const mapActivityRows = (activities = []) =>
  (Array.isArray(activities) ? activities : []).map((activity, index) => ({
    key: `${activity?.clientName || "activity"}-${index}`,
    clientName: activity?.clientName || "—",
    assignDate: formatDashboardDate(activity?.startDate) || "—",
    unassignDate: formatDashboardDate(activity?.endDate) || "—",
    expectation: formatDisplayValue(activity?.expected),
    currentPipeline: formatDisplayValue(activity?.currentCandidates),
  }));

const mapReviewCards = (reviews = []) =>
  (Array.isArray(reviews) ? reviews : []).map((review, index) => ({
    key: review?.clientId || `${review?.clientName || "review"}-${index}`,
    clientId: String(review?.clientId ?? "").trim(),
    reviewStatusId: String(review?.reviewStatusId ?? "").trim(),
    title: review?.clientName || "—",
    tone: resolveReviewCardTone(review?.reviewStatusId),
    stats: [
      {
        label: "Target",
        value: formatTargetRange(review?.incentiveTargetMin, review?.incentiveTargetMax),
      },
      {
        label: "This Month",
        value: formatDisplayValue(review?.currentAchieved ?? 0),
      },
      {
        label: "Last Month",
        value: formatDisplayValue(review?.lastAchieved ?? 0),
      },
    ],
    details: [
      { label: "TimeZone", value: formatTimezoneLabel(review?.timeZone) },
      { label: "Update Sent", value: formatLastReportSendRelative(review?.lastReportSend) },
    ],
    review,
  }));

const buildClientMetricsRows = (clientMetrics = [], clientId = "", statusIndex = []) => {
  const normalizedClientId = String(clientId ?? "").trim();
  const clientRows = (Array.isArray(clientMetrics) ? clientMetrics : []).filter(
    (row) => String(row?.clientId ?? "").trim() === normalizedClientId
  );

  const metricsByStatus = new Map(
    clientRows.map((row) => [String(row?.status ?? "").trim(), row])
  );

  const firstRow = clientRows[0] || {};

  return (Array.isArray(statusIndex) ? statusIndex : []).map((status) => {
    const metric = metricsByStatus.get(String(status ?? "").trim()) || {};
    return {
      status,
      last: formatMetricCell(metric?.last),
      current: formatMetricCell(metric?.current),
      targetMin: formatMetricCell(metric?.incentiveTargetMin ?? firstRow?.incentiveTargetMin),
      targetMax: formatMetricCell(metric?.incentiveTargetMax ?? firstRow?.incentiveTargetMax),
    };
  });
};

const BackendClientDetailView = ({
  review,
  dashboardData,
  onBack,
}) => {
  const navigate = useNavigate();
  const previousMonthLabel = dashboardData?.previousMonthLabel || "Previous";
  const currentMonthLabel = dashboardData?.currentMonthLabel || "Current";
  const statusIndex = dashboardData?.statusIndex || [];

  const metricsRows = useMemo(
    () =>
      buildClientMetricsRows(
        dashboardData?.clientMetrics,
        review?.clientId,
        statusIndex
      ),
    [dashboardData?.clientMetrics, review?.clientId, statusIndex]
  );

  const targetMin = metricsRows[0]?.targetMin || formatMetricCell(review?.incentiveTargetMin);
  const targetMax = metricsRows[0]?.targetMax || formatMetricCell(review?.incentiveTargetMax);
  const reviewStatusTone = resolveReviewCardTone(review?.reviewStatusId);

  const handleNavigateToToday = (cardPrefill = {}) => {
    const clientId = String(review?.clientId ?? "").trim();
    const clientName = String(review?.clientName ?? "").trim();
    if (!clientId) {
      toast.error("Client id is missing");
      return;
    }
    const prefillFilters = {
      clientId,
      clientIdLabel: clientName,
      clientName,
      ...cardPrefill,
    };
    try {
      sessionStorage.setItem(
        CALL_EXECUTIVE_TODAY_PREFILL_STORAGE_KEY,
        JSON.stringify(prefillFilters)
      );
    } catch {
      // ignore storage failures; navigation state remains the fallback
    }
    navigate(`/${urlEndPoint.today}`, {
      state: { prefillFilters },
    });
  };

  const headerInfoItems = [
    {
      key: "location",
      icon: "bx bx-map",
      label: "Location",
      value: formatTimezoneLabel(review?.timeZone),
    },
    {
      key: "update",
      icon: "bx bx-time-five",
      label: "Update Sent",
      value: formatLastReportSendRelative(review?.lastReportSend),
    },
    {
      key: "target",
      icon: "bx bx-target-lock",
      label: "Target",
      value: formatTargetRange(review?.incentiveTargetMin, review?.incentiveTargetMax),
    },
    {
      key: "achieved",
      icon: "bx bx-trophy",
      label: "Achieved",
      value: `This Month ${formatAchievedValue(review?.currentAchieved)} · Last Month ${formatAchievedValue(review?.lastAchieved)}`,
    },
    {
      key: "requirements",
      icon: "bx bx-list-check",
      label: "Requirements",
      value: formatDisplayValue(review?.requirements),
    },
  ];

  return (
    <div className="page-content">
                <Container fluid>
    <div className="recruiter-client-detail">
      <Card
        className={`recruiter-client-detail__header-card recruiter-client-detail__header-card--${reviewStatusTone} border-0`}
      >
        {/* <div className="recruiter-client-detail__header-accent" aria-hidden /> */}
        <CardBody className="recruiter-client-detail__header-body">
          <div className="recruiter-client-detail__header-top">
            <div className="recruiter-client-detail__identity">
              {/* <div className="recruiter-client-detail__avatar" aria-hidden>
                {String(review?.clientName || "?").charAt(0).toUpperCase()}
              </div> */}
              <div className="recruiter-client-detail__identity-text">
                <h4 className="recruiter-client-detail__title">{review?.clientName || "—"}</h4>
                {/* <span
                  className={`recruiter-client-detail__status-badge recruiter-client-detail__status-badge--${reviewStatusTone}`}
                >
                  {reviewStatusLabel}
                </span> */}
              </div>
            </div>
            <div className="recruiter-client-detail__toolbar">
              <Button type="button" color="light" className="recruiter-client-detail__back" onClick={onBack}>
                Back
              </Button>
              <Button type="button" color="primary" className="recruiter-client-detail__funnl">
                View In Funnl
              </Button>
            </div>
          </div>

          <div className="recruiter-client-detail__info-grid">
            {headerInfoItems.map((item) => (
              <div key={item.key} className="recruiter-client-detail__info-item">
                <div className="recruiter-client-detail__info-icon" aria-hidden>
                  <i className={item.icon} />
                </div>
                <div className="recruiter-client-detail__info-content">
                  <span className="recruiter-client-detail__info-label">{item.label}</span>
                  <span className="recruiter-client-detail__info-value">{item.value}</span>
                </div>
              </div>
            ))}
          </div>

          {review?.notes ? (
            <div className="recruiter-client-detail__notes">
              <div className="recruiter-client-detail__notes-icon" aria-hidden>
                <i className="bx bx-note" />
              </div>
              <div>
                <span className="recruiter-client-detail__notes-label">Notes</span>
                <p className="recruiter-client-detail__notes-text mb-0">{review.notes}</p>
              </div>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <Row className="recruiter-status-cards g-4">
        {STATIC_STATUS_CARDS.map((card) => (
          <Col xl="3" md="6" key={card.key}>
            <div
              role="button"
              tabIndex={0}
              className={`recruiter-status-cards__item recruiter-status-cards__item--${card.tone} recruiter-status-cards__item--clickable`}
              onClick={() => handleNavigateToToday(card.prefill)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleNavigateToToday(card.prefill);
                }
              }}
            >
                <div className="recruiter-status-cards__content">
                  <div className="recruiter-status-cards__title">{card.title}</div>
                  {card.subtitle ? (
                    <div className="recruiter-status-cards__subtitle">{card.subtitle}</div>
                  ) : null}
                  {/* {!card.staticValue ? (
                    <div className="recruiter-status-cards__count">{formatDisplayValue(rawValue ?? 0)}</div>
                  ) : null} */}
                </div>
                <span className="recruiter-status-cards__orb" aria-hidden />
              </div>
            </Col>
        ))}
      </Row>

      <Card className="recruiter-client-metrics-card border-0">
        <CardBody className="p-0">
          <div className="recruiter-client-metrics-table-wrap">
            <Table responsive className="recruiter-client-metrics-table mb-0">
              <thead>
                <tr>
                  {statusIndex.map((status) => (
                    <th key={status} colSpan={2} className="recruiter-client-metrics-table__group">
                      {status}
                    </th>
                  ))}
                  <th colSpan={2} className="recruiter-client-metrics-table__group">
                    Target
                  </th>
                </tr>
                <tr>
                  {statusIndex.map((status) => (
                    <React.Fragment key={`${status}-sub`}>
                      <th>{previousMonthLabel}</th>
                      <th>{currentMonthLabel}</th>
                    </React.Fragment>
                  ))}
                  <th>Min</th>
                  <th>Max</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  {metricsRows.map((row) => (
                    <React.Fragment key={row.status}>
                      <td>{row.last}</td>
                      <td>{row.current}</td>
                    </React.Fragment>
                  ))}
                  <td>{targetMin}</td>
                  <td>{targetMax}</td>
                </tr>
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
    </div>
    </Container>
    </div>
  );
};

const BackendDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activityRows, setActivityRows] = useState([]);
  const [reviewCards, setReviewCards] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchBackendDashboard();

      if (response?.success === false) {
        toast.error(response?.message || "Unable to load backend dashboard");
        setActivityRows([]);
        setReviewCards([]);
        setDashboardData(null);
        return;
      }

      const data = response?.data ?? {};
      setDashboardData(data);
      setActivityRows(mapActivityRows(data?.activities));
      setReviewCards(mapReviewCards(data?.reviews));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const listReview = useMemo(() => {
    if (!selectedClientId) return null;
    return (
      reviewCards.find((card) => card.clientId === selectedClientId)?.review ||
      (Array.isArray(dashboardData?.reviews)
        ? dashboardData.reviews.find(
            (item) => String(item?.clientId ?? "").trim() === selectedClientId
          )
        : null)
    );
  }, [selectedClientId, reviewCards, dashboardData?.reviews]);

  const selectedReview = useMemo(() => {
    if (!selectedClientId) return null;
    const detailsPayload =
      detailData && typeof detailData === "object" ? detailData : null;
    if (!detailsPayload) return listReview;

    return {
      ...(listReview && typeof listReview === "object" ? listReview : {}),
      ...detailsPayload,
      clientId: String(
        detailsPayload?.clientId ?? listReview?.clientId ?? ""
      ).trim(),
      clientName:
        detailsPayload?.clientName ??
        listReview?.clientName ??
        detailsPayload?.name,
    };
  }, [selectedClientId, detailData, listReview]);

  const detailDashboardData = useMemo(() => {
    const detailsPayload =
      detailData && typeof detailData === "object" ? detailData : null;
    if (!detailsPayload) return dashboardData;

    return {
      ...(dashboardData && typeof dashboardData === "object" ? dashboardData : {}),
      clientMetrics:
        detailsPayload?.clientMetrics ?? dashboardData?.clientMetrics,
      statusIndex: detailsPayload?.statusIndex ?? dashboardData?.statusIndex,
      previousMonthLabel:
        detailsPayload?.previousMonthLabel ?? dashboardData?.previousMonthLabel,
      currentMonthLabel:
        detailsPayload?.currentMonthLabel ?? dashboardData?.currentMonthLabel,
    };
  }, [detailData, dashboardData]);

  const loadClientDetails = useCallback(async (reviewStatusId) => {
    const id = String(reviewStatusId ?? "").trim();
    if (!id) return;

    setDetailLoading(true);
    setDetailData(null);

    try {
      const response = await fetchBackendDashboardDetails(id);
      if (response?.success === false) {
        toast.error(response?.message || "Unable to load client details");
        setSelectedClientId(null);
        return;
      }
      setDetailData(response?.data ?? {});
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleReviewCardClick = (card = {}) => {
    const clientId = String(card?.clientId ?? "").trim();
    const reviewStatusId = String(card?.reviewStatusId ?? "").trim();
    if (!reviewStatusId) {
      toast.error("Review status is missing");
      return;
    }
    setSelectedClientId(clientId || null);
    // loadClientDetails(reviewStatusId);
  };

  const handleBackToList = () => {
    setSelectedClientId(null);
    setDetailData(null);
    setDetailLoading(false);
  };

  if (loading) {
    return <Loaders />;
  }

  if (selectedClientId && detailLoading) {
    return <Loaders />;
  }

  if (selectedClientId && selectedReview) {
    return (
      <div className="recruiter-dashboard-root">
        <BackendClientDetailView
          review={selectedReview}
          dashboardData={detailDashboardData}
          onBack={handleBackToList}
        />
      </div>
    );
  }

  if (selectedClientId && !selectedReview) {
    return (
      <div className="recruiter-dashboard-root">
        <div className="recruiter-client-detail__toolbar">
          <Button type="button" color="light" onClick={handleBackToList}>
            Back
          </Button>
        </div>
        <p className="recruiter-empty-state">Client details not found.</p>
      </div>
    );
  }

  return (
    <div className="page-content">
                <Container fluid>
    <div className="recruiter-dashboard-root">
      <Card className="recruiter-activities-card border-0">
        <CardBody className="p-0 recruiter-activities-table-wrap">
          <Table responsive className="recruiter-activities-table mb-0" hover>
            <thead>
              <tr>
                <th className="recruiter-activities-table__col-client">Client</th>
                <th>Assign Date</th>
                <th>UnAssign Date</th>
                <th>Expectation</th>
                <th className="recruiter-activities-table__col-pipeline">Cur. Pipeline</th>
              </tr>
            </thead>
            <tbody>
              {activityRows.length ? (
                activityRows.map((row, index) => (
                  <tr
                    key={row.key}
                    className={index % 2 === 1 ? "recruiter-activities-table__row-alt" : ""}
                  >
                    <td>
                      <div className="recruiter-activity-client">
                        <span className="recruiter-activity-client__name">{row.clientName}</span>
                      </div>
                    </td>
                    <td>
                      <span className="recruiter-activity-date">{row.assignDate}</span>
                    </td>
                    <td>
                      <span
                        className={`recruiter-activity-date${
                          row.unassignDate === "—" ? " recruiter-activity-date--muted" : ""
                        }`}
                      >
                        {row.unassignDate}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`recruiter-activity-chip${
                          row.expectation === "—" ? " recruiter-activity-chip--muted" : ""
                        }`}
                      >
                        {row.expectation}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`recruiter-activity-pipeline${
                          row.currentPipeline === "—" ? " recruiter-activity-pipeline--empty" : ""
                        }`}
                      >
                        {row.currentPipeline}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="recruiter-empty-state">
                    No client activities found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>

      <div className="recruiter-section-header px-0 pt-0 border-0 bg-transparent shadow-none">
        <h5 className="recruiter-section-header__title">Client Reviews</h5>
        <span className="recruiter-section-header__meta">
          {reviewCards.length} {reviewCards.length === 1 ? "review" : "reviews"}
        </span>
      </div>

      <Row className="recruiter-review-grid g-3">
        {reviewCards.length ? (
          reviewCards.map((item) => (
            <Col xl="3" lg="4" md="6" key={item.key}>
              <Card
                role="button"
                tabIndex={0}
                className={`recruiter-review-card recruiter-review-card--clickable recruiter-review-card--${item.tone}`}
                onClick={() => handleReviewCardClick(item)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleReviewCardClick(item);
                  }
                }}
              >
                <div className="recruiter-review-card__head">
                  <h5 className="recruiter-review-card__title mb-4">{item.title}</h5>
                </div>
                <CardBody className="recruiter-review-card__body">
                  <div className="recruiter-review-stats">
                    {item.stats.map((stat) => (
                      <div key={stat.label} className="recruiter-review-stat">
                        <span className="recruiter-review-stat__label">{stat.label}</span>
                        <span className="recruiter-review-stat__value">{stat.value}</span>
                      </div>
                    ))}
                  </div>
                  <ul className="recruiter-review-details">
                    {item.details.map((detail) => (
                      <li key={detail.label} className="recruiter-review-detail">
                        <div className="recruiter-review-detail__content">
                          <span className="recruiter-review-detail__label">{detail.label}</span>
                          <span className="recruiter-review-detail__value">{detail.value}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardBody>
              </Card>
            </Col>
          ))
        ) : (
          <Col xs="12">
            <p className="recruiter-empty-state mb-0">No client reviews found.</p>
          </Col>
        )}
      </Row>
    </div>
    </Container>
    </div>
  );
};

export default BackendDashboard;
